/** SUNTRONIX ONE — Public "View & Accept Quotation" page.
 *  Unauthenticated, mobile-first page a customer opens from the one-tap
 *  share link.  They review the quote summary + PDF and either ACCEPT
 *  (name / designation / email / optional PO — full audit trail server-side)
 *  or DECLINE with a reason.  Nothing else is exposed. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AcceptServicesChecklist } from "@/components/public/AcceptServicesChecklist";
import { AcceptServicesPicker, initServices, servicesMissing, servicesPayload } from "@/components/public/AcceptServicesPicker";
import {
  CheckCircle2, AlertTriangle, FileText, Download, ShieldCheck,
  Building2, CalendarClock, Loader2, XCircle,
} from "lucide-react";

const ACCENT = "#B7803F";

function money(sym, v) {
  const n = Number(v || 0);
  return sym + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return String(d).slice(0, 10); }
}

export default function PublicQuoteAccept() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("view");        // view | decline
  const [done, setDone] = useState(null);          // {decision}
  const [f, setF] = useState({ accepted_by_name: "", designation: "", email: "", company: "", po_number: "", note: "", agreed: false });
  const [svc, setSvc] = useState({});
  const svcList = data?.services_checklist || [];
  const svcMissing = servicesMissing(svcList, svc);
  const [dc, setDc] = useState({ name: "", email: "", reason: "" });

  const pdfUrl = `${API}/public/quote/${token}/pdf`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/quote/${token}`);
        setData(data);
        setF((s) => ({ ...s, company: data?.quote?.customer_name || "" }));
        if (data?.already_decided) setDone({ decision: data.already_decided.decision });
      } catch (e) {
        setError(e?.response?.data?.detail || "This link is invalid or has expired.");
      }
    })();
  }, [token]);

  const accept = async () => {
    if (!f.accepted_by_name.trim()) return toast.error("Please enter your full name.");
    if (!f.agreed) return toast.error("Please tick the confirmation box to accept.");
    if (svcMissing.length) return toast.error(`Please mark each service as Required or Not required: ${svcMissing.join(", ")}`);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/quote/${token}/accept`, { ...f, services: servicesPayload(svc) });
      setDone({ decision: data.status === "pending_confirmation" ? "PENDING" : "ACCEPTED" });
      toast.success(data.status === "pending_confirmation" ? "Thank you — your confirmation has been received. SUNTRONIX will verify it and confirm your order." : "This quotation is already accepted — thank you.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record your acceptance.");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!dc.name.trim()) return toast.error("Please enter your name.");
    if (!dc.reason.trim()) return toast.error("Please add a short reason.");
    setBusy(true);
    try {
      await axios.post(`${API}/public/quote/${token}/decline`, dc);
      setDone({ decision: "DECLINED" });
      toast.success("Thank you — we've noted your response.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your response.");
    } finally { setBusy(false); }
  };

  if (error) return <Shell><Center testid="pqa-error"><AlertTriangle className="h-8 w-8 text-amber-500 mx-auto" strokeWidth={1.5} /><div className="mt-3 text-sm text-slate-600">{error}</div></Center></Shell>;
  if (!data) return <Shell><Center testid="pqa-loading"><Loader2 className="h-6 w-6 text-slate-400 mx-auto animate-spin" /><div className="mt-3 text-sm text-slate-500">Loading your quotation…</div></Center></Shell>;

  const q = data.quote;
  const sym = q.currency_symbol || "₹";

  if (done) {
    const accepted = done.decision === "ACCEPTED" || done.decision === "PENDING";
    const pending = done.decision === "PENDING";
    return (
      <Shell>
        <Center testid="pqa-done">
          {accepted
            ? <CheckCircle2 className="h-12 w-12 mx-auto" strokeWidth={1.4} style={{ color: "#059669" }} />
            : <XCircle className="h-12 w-12 text-slate-400 mx-auto" strokeWidth={1.4} />}
          <div className="mt-4 text-lg font-semibold text-slate-900" data-testid="pqa-done-title">
            {pending ? "Confirmation received" : accepted ? "Quotation accepted" : "Response recorded"}
          </div>
          <div className="mt-1.5 text-[13px] text-slate-500">
            {pending
              ? <>Thank you. Your confirmation of <b>{q.code}</b> has been received. SUNTRONIX will verify it and confirm your order.</>
              : accepted
              ? <>Thank you. Your acceptance of <b>{q.code}</b> has been recorded and the SUNTRONIX team has been notified. They'll be in touch shortly.</>
              : <>Thank you for letting us know. The SUNTRONIX team will follow up.</>}
          </div>
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="pqa-done-download"
            className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium" style={{ color: ACCENT }}>
            <Download className="h-4 w-4" /> Download your quotation PDF
          </a>
        </Center>
        {accepted && (data?.services_checklist || []).some((s) => !s.answered) && <div className="mx-auto -mt-16 max-w-2xl px-4 pb-10"><AcceptServicesChecklist api={API} token={token} checklist={data.services_checklist} name={f.accepted_by_name} email={f.email} after /></div>}
      </Shell>
    );
  }

  const locked = data.is_terminal || !!data.accept_blocked_reason;

  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="pqa-page">
        {/* header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">SUNTRONIX</div>
            <div className="text-xl font-semibold text-slate-900 mt-0.5">Your Quotation</div>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <ShieldCheck className="h-4 w-4" style={{ color: ACCENT }} /> Secure link
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-4 mt-5 min-w-0">
          {/* LEFT — summary + PDF */}
          <div className="md:col-span-3 space-y-4 min-w-0">
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="pqa-summary">
              <div className="px-4 py-3 text-white flex items-center justify-between gap-3" style={{ background: "#0f172a" }}>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-white/50">Quotation</div>
                  <div className="font-semibold truncate" data-testid="pqa-code">{q.code}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wider text-white/50">Total (incl. taxes)</div>
                  <div className="font-semibold" data-testid="pqa-total">{money(sym, q.grand_total_inr)}</div>
                </div>
              </div>
              <div className="px-4 py-3 grid grid-cols-2 gap-y-2 gap-x-4 text-[12px]">
                <Meta icon={Building2} label="Prepared for" value={q.customer_name || "—"} />
                <Meta icon={CalendarClock} label="Valid until" value={fmtDate(q.valid_until)} />
                {q.contact_name && <Meta label="Attention" value={q.contact_name} />}
                {q.subject && <Meta label="Subject" value={q.subject} />}
              </div>
              {/* line items */}
              <div className="border-t border-slate-100">
                {(q.items || []).map((it, i) => (
                  <div key={i} className="px-4 py-2.5 flex items-start justify-between gap-3 border-b border-slate-50 last:border-0" data-testid={`pqa-item-${i}`}>
                    <div className="min-w-0">
                      <div className="text-[13px] text-slate-800 truncate">{it.product_name}</div>
                      <div className="text-[11px] text-slate-400">Qty {it.quantity} · {money(sym, it.unit_price_inr)} · GST {it.tax_pct}%</div>
                    </div>
                    <div className="text-[13px] font-medium text-slate-800 whitespace-nowrap">{money(sym, it.line_total_inr)}</div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-3 bg-slate-50 text-[12px] space-y-1">
                <Row label="Subtotal" value={money(sym, q.subtotal_inr)} />
                <Row label="Taxes" value={money(sym, q.tax_inr)} />
                <div className="flex justify-between pt-1 border-t border-slate-200 font-semibold text-slate-900">
                  <span>Grand Total</span><span>{money(sym, q.grand_total_inr)}</span>
                </div>
              </div>
            </div>

            <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="pqa-view-pdf"
              className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              <FileText className="h-4 w-4" style={{ color: ACCENT }} /> View / download the full quotation PDF
            </a>
            <iframe title="Quotation PDF" src={pdfUrl} data-testid="pqa-pdf-frame"
              className="hidden md:block w-full h-[520px] rounded-xl border border-slate-200 bg-white" />
          </div>

          {/* RIGHT — decision */}
          <div className="md:col-span-2 min-w-0">
            <div className="rounded-xl border border-slate-200 bg-white p-4 md:sticky md:top-6" data-testid="pqa-decision">
              {locked ? (
                <div className="text-center py-6" data-testid="pqa-locked">
                  <AlertTriangle className="h-7 w-7 text-amber-500 mx-auto" strokeWidth={1.5} />
                  <div className="mt-2 text-[13px] text-slate-600" data-testid="pqa-locked-reason">{data.accept_blocked_reason || <>This quotation is <b>{q.status}</b> and is no longer open for acceptance. Please contact SUNTRONIX.</>}</div>
                </div>
              ) : mode === "view" ? (
                <>
                  <div className="text-[15px] font-semibold text-slate-900">Accept this quotation</div>
                  <div className="text-[12px] text-slate-500 mt-0.5">Confirm your details to formally accept {q.code}.</div>
                  <div className="mt-3 space-y-2.5">
                    <Field label="Full name *" value={f.accepted_by_name} onChange={(v) => setF({ ...f, accepted_by_name: v })} testid="pqa-name" />
                    <Field label="Designation" value={f.designation} onChange={(v) => setF({ ...f, designation: v })} testid="pqa-designation" />
                    <Field label="Work email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="pqa-email" />
                    <Field label="Your PO number (optional)" value={f.po_number} onChange={(v) => setF({ ...f, po_number: v })} testid="pqa-po" />
                    <div>
                      <Label className="text-[11px] text-slate-500">Note (optional)</Label>
                      <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="pqa-note" className="mt-1" />
                    </div>
                    <AcceptServicesPicker checklist={svcList} state={svc} setState={setSvc} lookup={(org) => axios.post(`${API}/public/quote/${token}/abm-lookup`, { org_id: org }).then((r) => r.data)} />
                    <label className="flex items-start gap-2 text-[12px] text-slate-600 cursor-pointer select-none pt-1">
                      <input type="checkbox" checked={f.agreed} onChange={(e) => setF({ ...f, agreed: e.target.checked })}
                        data-testid="pqa-agree" className="mt-0.5" />
                      I confirm I am authorised to accept this quotation on behalf of {q.customer_name || "our organisation"}, and I accept the pricing and terms in the attached PDF.
                    </label>
                  </div>
                  <Button onClick={accept} disabled={busy || svcMissing.length > 0} data-testid="pqa-accept-btn"
                    className="w-full mt-3 text-white" style={{ background: busy ? "#94a3b8" : "#059669" }}>
                    {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Recording…</> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Accept quotation</>}
                  </Button>
                  <button onClick={() => setMode("decline")} data-testid="pqa-decline-open"
                    className="w-full mt-2 text-[12px] text-slate-500 hover:text-slate-700 py-1.5">
                    I can't accept this right now
                  </button>
                </>
              ) : (
                <div data-testid="pqa-decline-form">
                  <div className="text-[15px] font-semibold text-slate-900">Let us know</div>
                  <div className="text-[12px] text-slate-500 mt-0.5">We'll pass this to the SUNTRONIX team.</div>
                  <div className="mt-3 space-y-2.5">
                    <Field label="Your name *" value={dc.name} onChange={(v) => setDc({ ...dc, name: v })} testid="pqa-decline-name" />
                    <Field label="Work email" value={dc.email} onChange={(v) => setDc({ ...dc, email: v })} testid="pqa-decline-email" />
                    <div>
                      <Label className="text-[11px] text-slate-500">Reason *</Label>
                      <Textarea rows={3} value={dc.reason} onChange={(e) => setDc({ ...dc, reason: e.target.value })} data-testid="pqa-decline-reason" className="mt-1" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" onClick={() => setMode("view")} className="flex-1" data-testid="pqa-decline-back">Back</Button>
                    <Button onClick={decline} disabled={busy} className="flex-1 text-white" style={{ background: "#334155" }} data-testid="pqa-decline-submit">
                      {busy ? "Submitting…" : "Submit"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="text-[10px] text-slate-400 text-center mt-3">
              Link expires on {fmtDate(data.expires_at)} · SUNTRONIX since {data.vendor?.since || "1999"}
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => <div className="min-h-screen bg-slate-50 overflow-x-hidden">{children}</div>;
const Center = ({ children, testid }) => (
  <div className="min-h-screen flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid={testid}>{children}</div>
  </div>
);
const Meta = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-1.5">
    {Icon && <Icon className="h-3.5 w-3.5 text-slate-400 mt-0.5" strokeWidth={1.5} />}
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-slate-700 truncate">{value}</div>
    </div>
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between text-slate-600"><span>{label}</span><span>{value}</span></div>
);
const Field = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-[11px] text-slate-500">{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 h-9" />
  </div>
);
